import { BrowserRouter, Routes, Route } from "react-router-dom";
import React, { Suspense, lazy } from "react";
import { Page } from "./types";
import ScrollToTop from "./ScrollToTop";
import useWindowSize from "hooks/useWindowResize";
import Header from "components/Header/Header";
import withTracker from "utils/withTracker";

// Lazy loaded components
const LazyFooter = lazy(() => import("shared/Footer/LazyFooter"));
const FooterNav = lazy(() => import("components/FooterNav"));

// Lazy loaded pages - grouped by features for better chunking
// Home and Core Pages
const PageHome = lazy(() => import(/* webpackChunkName: "home" */ "containers/PageHome/PageHome"));
const Page404 = lazy(() => import(/* webpackChunkName: "errors" */ "containers/Page404/Page404"));

// Listing Pages
const ListingStayPage = lazy(() => import(/* webpackChunkName: "listings" */ "containers/ListingStayPage/ListingStayPage"));
const ListingsPage = lazy(() => import(/* webpackChunkName: "listings" */ "containers/ListingStayPage/ListingsPage"));
const ListingStayDetailPage = lazy(() => import(/* webpackChunkName: "listing-detail" */ "containers/ListingDetailPage/listing-stay-detail/ListingStayDetailPage"));

// Checkout & Payment
const CheckOutPage = lazy(() => import(/* webpackChunkName: "payment" */ "containers/CheckOutPage/CheckOutPage"));
const PaymentPage = lazy(() => import(/* webpackChunkName: "payment" */ "containers/PaymentPage/PaymentPage"));
const ReturnPage = lazy(() => import(/* webpackChunkName: "payment" */ "containers/PaymentPage/ReturnPage"));
const CancelledPaymentPage = lazy(() => import(/* webpackChunkName: "payment" */ "containers/PaymentPage/CancelledPaymentPage"));

// Account
const AccountPage = lazy(() => import(/* webpackChunkName: "account" */ "containers/AccountPage/AccountPage"));
const AccountPass = lazy(() => import(/* webpackChunkName: "account" */ "containers/AccountPage/AccountPass"));
const MyBookings = lazy(() => import(/* webpackChunkName: "account" */ "containers/MyBookings/MyBookings"));
const PageSubcription = lazy(() => import(/* webpackChunkName: "account" */ "containers/PageSubcription/PageSubcription"));

// Auth
const PageSignUp = lazy(() => import(/* webpackChunkName: "auth" */ "containers/PageSignUp/PageSignUp"));
const PageLogin = lazy(() => import(/* webpackChunkName: "auth" */ "containers/PageLogin/PageLogin"));
const ForgetPassword = lazy(() => import(/* webpackChunkName: "auth" */ "components/ForgetPassword"));
const ForgetPassRequestLink = lazy(() => import(/* webpackChunkName: "auth" */ "components/ForgetPassRequestLink"));

// Info Pages
const PageContact = lazy(() => import(/* webpackChunkName: "info" */ "containers/PageContact/PageContact"));
const PageAbout = lazy(() => import(/* webpackChunkName: "info" */ "containers/PageAbout/PageAbout"));
const PageMember = lazy(() => import(/* webpackChunkName: "info" */ "containers/PageMember/PageMember"));
const PageTerms = lazy(() => import(/* webpackChunkName: "legal" */ "containers/PageTerms/PageTerms"));
const PagePrivacy = lazy(() => import(/* webpackChunkName: "legal" */ "containers/PagePrivacy/PagePrivacy"));

// Apply the withTracker HOC to all lazy-loaded page components
const TrackedPageHome = withTracker(PageHome);
const TrackedListingStayPage = withTracker(ListingStayPage);
const TrackedListingsPage = withTracker(ListingsPage);
const TrackedListingStayDetailPage = withTracker(ListingStayDetailPage);
const TrackedCheckOutPage = withTracker(CheckOutPage);
const TrackedAccountPage = withTracker(AccountPage);
const TrackedAccountPass = withTracker(AccountPass);
const TrackedForgetPassRequestLink = withTracker(ForgetPassRequestLink);
const TrackedPageContact = withTracker(PageContact);
const TrackedPageAbout = withTracker(PageAbout);
const TrackedPageMember = withTracker(PageMember);
const TrackedPaymentPage = withTracker(PaymentPage);
const TrackedReturnPage = withTracker(ReturnPage);
const TrackedCancelledPaymentPage = withTracker(CancelledPaymentPage);
const TrackedPageSignUp = withTracker(PageSignUp);
const TrackedPageLogin = withTracker(PageLogin);
const TrackedPageSubcription = withTracker(PageSubcription);
const TrackedForgetPassword = withTracker(ForgetPassword);
const TrackedMyBookings = withTracker(MyBookings);
const TrackedPage404 = withTracker(Page404);
const TrackedPageTerms = withTracker(PageTerms);
const TrackedPagePrivacy = withTracker(PagePrivacy);

export const pages: Page[] = [
  { path: "/", exact: true, component: TrackedPageHome },
  { path: "/#", exact: true, component: TrackedPageHome },
  //
  { path: "/listing-stay", component: TrackedListingStayPage },
  { path: "/listing-stay-map", component: TrackedListingsPage },
  { path: "/listing-stay-detail/:id", component: TrackedListingStayDetailPage },
  //
  { path: "/checkout", component: TrackedCheckOutPage },
  //
  { path: "/account", component: TrackedAccountPage },
  { path: "/account-password", component: TrackedAccountPass },
  { path: "/forgot-password", component: TrackedForgetPassRequestLink },
  { path: "/my-subscription", component: TrackedPaymentPage },
  { path: "/my-bookings", component: TrackedMyBookings },
  //
  { path: "/contact", component: TrackedPageContact },
  { path: "/about", component: TrackedPageAbout },
  { path: "/member", component: TrackedPageMember },
  { path: "/payment-page", component: TrackedPaymentPage },
  { path: "/payment-status", component: TrackedReturnPage },
  { path: "/payment-status-cancelled", component: TrackedCancelledPaymentPage },
  { path: "/signup", component: TrackedPageSignUp },
  { path: "/login", component: TrackedPageLogin },
  { path: "/terms", component: TrackedPageTerms },
  { path: "/privacy", component: TrackedPagePrivacy },
  { path: "/subscription", component: TrackedPageSubcription },
  { path: "/reset-password/:token", component: TrackedForgetPassword },
];

// Loading spinner component for Suspense fallback
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Error boundary for lazy-loaded components
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean}> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Route Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen">
          <h2 className="text-2xl font-bold mb-4">Something went wrong</h2>
          <button 
            className="px-4 py-2 bg-primary text-white rounded"
            onClick={() => this.setState({ hasError: false })}
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const MyRoutes = () => {
  let WIN_WIDTH = useWindowSize().width;
  if (typeof window !== "undefined") {
    WIN_WIDTH = WIN_WIDTH || window.innerWidth;
  }

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Header />
      <ErrorBoundary>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {pages.map(({ component, path }) => {
              const Component = component;
              return <Route key={path} element={<Component />} path={path} />;
            })}
            <Route path="*" element={<TrackedPage404 />} />
          </Routes>

          {WIN_WIDTH < 768 && <FooterNav />}
          <LazyFooter rootMargin="200px" />
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default MyRoutes;
